import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import Editor from "@monaco-editor/react";
import axios from "axios";
import removeWhiteImg from "../../../assets/images/remove-icon.svg";
import rightImg from "../../../assets/images/right-white-icon.svg";
import whiteLogoImg from "../../../assets/images/needle-in-the-haystack-white-logo.svg";
import { useCodingRecording } from "../../../hooks/useCodingRecording";
import {
  getCodingSocket,
  resetCodingSocket,
} from "../../../hooks/codingSocket";
import { useMeetingAppContext } from "../../../hooks/MeetingAppContextDef";
import { useMeeting } from "@videosdk.live/react-sdk";
import { WarningModal } from "../../../components/commonComponents/WarningModal";

const WS_URL = "ws://localhost:5001/ws/coding";
// const JUDGE0_URL = "https://judge0-52-139-183-155.nip.io";

const JUDGE0_URL = process.env.REACT_APP_JUDGE0_URL;
const CodingOverlay = forwardRef(
  (
    {
      sessionId,
      meetingId,
      onSubmit,
      onRestart,
      setShowCoding,
      codingQuestions,
      questionIndex = 0,
      isReconnect = false,
      reconnectQuestionId = null,
      reconnectElapsedSeconds = 0,
      reconnectVideoIndex = 0,
      resetTimer,
    },
    ref,
  ) => {
    const { setSideBarMode } = useMeetingAppContext();
    const { localMicOn, toggleMic } = useMeeting();
    //console.log("codingQuestions", codingQuestions);
    // const ws = useRef(null);
    const latestCodeRef = useRef("");
    const codeDebounceRef = useRef(null);
    const latestLanguageRef = useRef("");
    const latestJudgeResultRef = useRef(null);

    const [languageId, setLanguageId] = useState(null);
    const [language, setLanguage] = useState(null);
    const [code, setCode] = useState("");
    const [stdin, setStdin] = useState("");
    const [output, setOutput] = useState("");
    const [status, setStatus] = useState("");
    const [running, setRunning] = useState(false);

    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showPasteWarning, setShowPasteWarning] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

    const totalQuestions = codingQuestions?.length || 1;
    const currentQuestion =
      codingQuestions?.[questionIndex] ?? codingQuestions?.[0];
    const isLastQuestion = questionIndex >= totalQuestions - 1;
    const questionId =
      (isReconnect && reconnectQuestionId) ||
      currentQuestion?.question_id ||
      null;

    // ── Socket ──
    const socket = getCodingSocket(
      sessionId,
      questionId,
      isSubmitting || showSubmitSuccess,
    );

    const monacoEditorRef = useRef(null);
    const internalClipboardRef = useRef("");
    const handleEditorMount = (editor) => {
      monacoEditorRef.current = editor;
    };

    // Initialize from question details
    useEffect(() => {
      if (currentQuestion) {
        let initialLangId = 71;
        let initialLang = "Python";
        // currentQuestion.language may come back as a string, an object
        // (e.g. { id, name }), or something else depending on the API
        // response — normalize it to a lowercase string before comparing.
        const rawLanguage = currentQuestion.language;
        const languageName = rawLanguage?.name ?? null;
        const languageId = rawLanguage?.id ?? null;

        setLanguageId(languageId || initialLangId);
        setCode(currentQuestion.code || "");
        setLanguage(languageName || initialLang);
        setStdin(getInputPlaceholder(currentQuestion?.sample_input));
      }
    }, [currentQuestion]);

    // Force a completely fresh WebSocket for every question
    useEffect(() => {
      return () => {
        // resetCodingSocket();
      };
    }, []);
    // ── Countdown timer (15 minutes = 900 s) ───────────────────────────────────
    const TIMER_DURATION_S = 15 * 60;
    const [timeLeft, setTimeLeft] = useState(() => {
      const reconnectPending =
        isReconnect ||
        sessionStorage.getItem("codingReconnectPending") === "true";
      if (!reconnectPending || resetTimer) {
        return TIMER_DURATION_S;
      }

      // Resume the countdown from where the last session left off:
      // 15min - coding_duration (seconds already spent), not a fresh 15min.
      const elapsed =
        reconnectElapsedSeconds ||
        Number(sessionStorage.getItem("coding_duration") || 0);
      return Math.max(TIMER_DURATION_S - elapsed, 0);
    });

    // On reconnect, video chunks must continue from coding_video_index
    // rather than restarting at 0 — otherwise a reconnect would overwrite
    // chunk 0 of the previous session instead of continuing the sequence.
    const initialVideoIndexRef = useRef(
      (() => {
        const reconnectPending =
          isReconnect ||
          sessionStorage.getItem("codingReconnectPending") === "true";
        if (!reconnectPending) return 0;
        return (
          reconnectVideoIndex ||
          Number(sessionStorage.getItem("coding_video_index") || 0)
        );
      })(),
    );
    const autoSubmittedRef = useRef(false);

    useEffect(() => {
      if (timeLeft <= 0) return;
      const tick = setInterval(() => setTimeLeft((s) => s - 1), 1000);
      return () => clearInterval(tick);
    }, [timeLeft]);

    useEffect(() => {
      if (timeLeft <= 0 && !autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        submitCoding();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeLeft]);

    const formatTime = (s) => {
      const m = Math.floor(s / 60)
        .toString()
        .padStart(2, "0");
      const sec = (s % 60).toString().padStart(2, "0");
      return `${m}:${sec}`;
    };
    const getInputPlaceholder = (sampleInput) => {
      if (!sampleInput) {
        return "Enter standard input...";
      }

      const entries = Object.entries(sampleInput).filter(
        ([, value]) => value !== null && value !== undefined,
      );

      if (entries.length === 0) {
        return "Enter standard input...";
      }

      // Single input
      if (entries.length === 1) {
        const [, value] = entries[0];

        if (typeof value === "string") {
          return value;
        }

        return JSON.stringify(value);
      }

      // Multiple inputs - each field on its own line
      return `{\n${entries
        .map(([key, value]) => `  "${key}": ${JSON.stringify(value)}`)
        .join(",\n")}\n}`;
    };
    const timerIsUrgent = timeLeft <= 30;

    // Play question audio if available on mount

    const audioRef = useRef(null);
    useEffect(() => {
      startCodingRecording();

      if (currentQuestion?.audio_base64) {
        const audio = new Audio(
          `data:audio/wav;base64,${currentQuestion.audio_base64}`,
        );

        audioRef.current = audio;
        audio.play().catch(console.warn);
      }

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current = null;
        }
      };
    }, [currentQuestion?.question_id]);

    const languageName = () => {
      switch (languageId) {
        case 71:
          return "python";
        case 63:
          return "javascript";
        case 62:
          return "java";
        case 54:
          return "cpp";
        case 50:
          return "c";
        default:
          return "python";
      }
    };

    useEffect(() => {
      latestCodeRef.current = code;
    }, [code]);
    useEffect(() => {
      latestLanguageRef.current = language;
    }, [language]);

    useEffect(() => {
      const hideResizeObserverError = (e) => {
        if (
          e.message === "ResizeObserver loop limit exceeded" ||
          e.message ===
            "ResizeObserver loop completed with undelivered notifications."
        ) {
          const resizeObserverErrDiv = document.getElementById(
            "webpack-dev-server-client-overlay-div",
          );
          const resizeObserverErr = document.getElementById(
            "webpack-dev-server-client-overlay",
          );
          if (resizeObserverErr) resizeObserverErr.style.display = "none";
          if (resizeObserverErrDiv) resizeObserverErrDiv.style.display = "none";
        }
      };
      window.addEventListener("error", hideResizeObserverError);
      return () => window.removeEventListener("error", hideResizeObserverError);
    }, []);

    // ── SOCKET CONNECT ─────────────────────────────────────────────────
    useEffect(() => {
      if (!meetingId || !socket) return;
      socket.connect();

      const handleReconnect = (data) => {
        console.log("Received Coding-reconnect:", data);
        if (data) {
          setCode(data);
        }
      };

      socket.on("Coding-reconnect", handleReconnect);
      socket.on("coding-reconnect", handleReconnect);

      if (isReconnect) {
        // socket.emit("coding-reconnect", {
        //   roomId: meetingId,
        //   boardId: sessionId,
        //   codingQuestionId: questionId,
        // });
      } else {
        // socket.emit("join-board", {
        //   roomId: meetingId,
        //   boardId: sessionId,
        //   questionId,
        // });
      }

      // socket.emit("start-recording", {
      //   sessionId,
      //   roomId: meetingId,
      //   boardId: sessionId,
      //   questionId,
      // });

      return () => {
        socket.off("Coding-reconnect", handleReconnect);
        socket.off("coding-reconnect", handleReconnect);
      };
    }, [meetingId, sessionId, socket, isReconnect, questionId]);

    useEffect(() => {
      if (!localMicOn) toggleMic();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── Video-recording pipeline hook ──────────────────────────────────
    const containerRef = useRef(null);
    const {
      isRecording,
      startCodingRecording,
      stopVideoRecording,
      stopAndSubmitCoding,
    } = useCodingRecording({
      interviewId: sessionStorage.getItem("interviewId") || "",
      sessionId,
      meetingId,
      containerRef,
      //wsRef: ws,
      getLanguage: () => latestLanguageRef.current,
      getCode: () => latestCodeRef.current,
      questionId,
      socket,
      startingChunkIndex: initialVideoIndexRef.current,
    });

    // useEffect(() => {
    //   const t = setTimeout(() => startCodingRecording(), 300);
    //   return () => clearTimeout(t);
    // }, [sessionId, startCodingRecording]);
    const [showSnackbar, setShowSnackbar] = useState(true);
    useEffect(() => {
      const t = setTimeout(() => setShowSnackbar(false), 3000);
      return () => clearTimeout(t);
    }, []);

    const onCodeChange = (value) => {
      latestCodeRef.current = value ?? "";
      clearTimeout(codeDebounceRef.current);
      codeDebounceRef.current = setTimeout(() => setCode(value ?? ""), 300);
    };

    const runCode = async () => {
      const currentCode = latestCodeRef.current.trim() || code.trim();
      if (!currentCode) {
        setValidationMessage(
          "Please make sure you have written your code before running.",
        );
        setShowValidationModal(true);
        return;
      }
      if (currentQuestion?.sample_input && !stdin.trim()) {
        setValidationMessage("Please enter the input before running.");
        setShowValidationModal(true);
        return;
      }

      setRunning(true);
      setOutput("");
      setStatus("");
      try {
        const response = await axios.post(
          `${JUDGE0_URL}/submissions?base64_encoded=false&wait=true`,
          {
            source_code: code,
            language_id: languageId,
            stdin: stdin.trim() || "",
          },
        );
        const result = response.data;
        latestJudgeResultRef.current = result;
        const statusText = result.status?.description || "Unknown";
        setStatus(statusText);
        let consoleOutput = "";
        consoleOutput += `Status : ${statusText}\n`;
        consoleOutput += `Time : ${result.time}\n`;
        consoleOutput += `Memory : ${result.memory}\n`;
        consoleOutput += "--------------------------------------\n";
        if (result.stdout) consoleOutput += result.stdout + "\n";
        if (result.stderr) consoleOutput += "\nSTDERR\n" + result.stderr + "\n";
        if (result.compile_output)
          consoleOutput += "\nCOMPILER OUTPUT\n" + result.compile_output;
        if (result.message) consoleOutput += "\nMESSAGE\n" + result.message;
        setOutput(consoleOutput);
      } catch (err) {
        console.error(err);
        setOutput("Judge0 Server Error");
      }
      setRunning(false);
    };
    const closeCoding = () => {
      setShowCoding(false);
      setSideBarMode("CHAT");
      if (onSubmit) onSubmit({ isLastQuestion: true, questionIndex });
    };
    const submitCoding = async () => {
      setIsSubmitting(true);
      try {
        const { lastVideoChunk, chunkIndex } = await stopAndSubmitCoding();

        socket.emit("submit-coding", {
          event: "submit-coding",
          sessionId,
          meetingId,
          language: latestLanguageRef.current,
          finalCode: latestCodeRef.current,
          judgeOutput: latestJudgeResultRef.current,
          lastVideoChunk,
          chunkIndex,
        });
      } catch (err) {
        console.error("[coding] Submit error (non-blocking):", err);
      } finally {
        setTimeout(() => {
          setIsSubmitting(false);
          setShowSubmitSuccess(true);

          setTimeout(() => {
            setShowSubmitSuccess(false);

            if (isLastQuestion) {
              closeCoding();
            } else {
              setTimeLeft(TIMER_DURATION_S);

              if (onSubmit) onSubmit({ isLastQuestion: false, questionIndex });
            }
          }, 2000);
        }, 3000);
      }
      // }
    };

    const triggerSubmitButton = () => {
      const currentCode = latestCodeRef.current.trim() || code.trim();
      if (!currentCode) {
        setValidationMessage(
          "Please make sure you have written your code before submitting.",
        );
        setShowValidationModal(true);
        return;
      }
      if (currentQuestion?.sample_input && !stdin.trim()) {
        setValidationMessage("Please enter the input before submitting.");
        setShowValidationModal(true);
        return;
      }
      if (!latestJudgeResultRef.current) {
        setValidationMessage(
          "Please run the code at least once before submitting.",
        );
        setShowValidationModal(true);
        return;
      }
      setShowConfirmationModal(true);
    };

    useImperativeHandle(ref, () => ({
      getCurrentCode() {
        return latestCodeRef.current;
      },
      getJudgeOutput() {
        return latestJudgeResultRef.current;
      },
      clearEditor() {
        setCode("");
      },
      getCurrentLanguage() {
        return latestLanguageRef.current;
      },
      submitCoding,
    }));

    const SubmitConfirmationModal = (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{ zIndex: 99999 }}
      >
        <div className="bg-white rounded-lg shadow-lg px-[80px] py-[24px]">
          <h3
            className="flex justify-center app-body font-medium mb-4"
            style={{ color: "#333" }}
          >
            Confirmation
          </h3>
          <p
            className="mb-4 text-pretty text-center app-textbox"
            style={{ color: "#666" }}
          >
            {isLastQuestion
              ? "Are you sure you want to submit?"
              : "Are you sure you want to submit this question and move to the next one?"}
          </p>
          <div className="flex justify-center space-x-4 mt-8">
            <button
              className="bg-cancelBgColor btn-size text-white px-6 py-1.5"
              style={{
                background: "#6b7280",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => setShowConfirmationModal(false)}
            >
              <span className="flex items-center">
                <img src={removeWhiteImg} alt="no" className="w-3 h-3 mr-2" />{" "}
                No
              </span>
            </button>
            <button
              className="bg-btnBgColor btn-size text-white px-6 py-1.5"
              style={{
                background: "#1a1a2e",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => {
                setShowConfirmationModal(false);
                submitCoding();
              }}
            >
              <span className="flex items-center">
                <img src={rightImg} alt="yes" className="w-4 h-4 mr-2" /> Yes
              </span>
            </button>
          </div>
        </div>
      </div>
    );

    return (
      <>
        {/* Pulse keyframe for urgent timer */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.55; }
          }
          .coding-topbar {
            flex-direction: row;
          }
          .coding-topbar-center {
            justify-content: center;
          }
          .coding-topbar-controls {
            justify-content: flex-end;
          }
          .coding-editor-container {
            flex-direction: row;
          }
          .coding-panel {
            width: 420px;
            border-left: 1px solid #444;
            border-top: none;
          }
          @media (max-width: 900px) {
            .coding-topbar {
              flex-direction: column !important;
              align-items: stretch !important;
              padding: 12px 16px !important;
              gap: 12px !important;
            }
            .coding-topbar-center {
              justify-content: flex-start !important;
              flex-wrap: wrap;
            }
            .coding-topbar-controls {
              justify-content: flex-start !important;
              flex-wrap: wrap;
            }
            .coding-editor-container {
              flex-direction: column !important;
            }
            .coding-panel {
              width: 100% !important;
              flex: 2;
              border-left: none !important;
              border-top: 1px solid #444 !important;
            }
          }
        `}</style>

        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            background: "#1e1e1e",
            overflow: "hidden",
            margin: 0,
            padding: 0,
            zIndex: 200,
          }}
        >
          {/* ── Top bar ── */}
          <div
            className="coding-topbar"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 20px",
              minHeight: 52,
              background: "#1a1a2e",
              flexShrink: 0,
              gap: 20,
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={whiteLogoImg}
                alt="Needle logo"
                style={{ height: 20, objectFit: "contain", flexShrink: 0 }}
              />
            </div>

            <div
              className="coding-topbar-center"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: 1,
              }}
            >
              <div
                style={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  lineHeight: "1.3",
                  textAlign: "center",
                }}
                title={currentQuestion?.title}
              >
                {currentQuestion?.title}
                {totalQuestions > 1 && (
                  <span
                    style={{
                      color: "#a0aec0",
                      fontWeight: 500,
                      fontSize: 12,
                      marginLeft: 8,
                      whiteSpace: "nowrap",
                    }}
                  >
                    (Question {questionIndex + 1} of {totalQuestions})
                  </span>
                )}
              </div>
              {/* <button
                onClick={() => setShowQuestionModal(true)}
                style={{
                  background: "transparent",
                  color: "#c8c8ff",
                  border: "1px solid #4a4a7e",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                View Question
              </button> */}
            </div>

            <div
              className="coding-topbar-controls"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              {/* ── Countdown timer ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: timerIsUrgent
                    ? "rgba(220,38,38,0.15)"
                    : "rgba(255,255,255,0.08)",
                  border: `1px solid ${timerIsUrgent ? "#ef4444" : "#4a4a7e"}`,
                  borderRadius: 8,
                  padding: "5px 14px",
                  animation: timerIsUrgent ? "pulse 1s infinite" : "none",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={timerIsUrgent ? "#ef4444" : "#c8c8ff"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span
                  style={{
                    color: timerIsUrgent ? "#ef4444" : "#c8c8ff",
                    fontWeight: 700,
                    fontSize: 14,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: 1,
                  }}
                >
                  {formatTime(timeLeft)}
                </span>
              </div>

              {/* Language Selection */}
              <select
                value={languageId || ""}
                disabled
                style={{
                  background: "#1a1a2e",
                  color: "#c8c8ff",
                  border: "1px solid #4a4a7e",
                  borderRadius: 8,
                  padding: "7px 12px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "not-allowed",
                  outline: "none",
                  opacity: 0.7,
                }}
              >
                <option value={languageId}>{language}</option>
              </select>

              {/* Run Code */}
              <button
                onClick={runCode}
                disabled={running}
                style={{
                  background: "transparent",
                  color: "#c8c8ff",
                  border: "1px solid #4a4a7e",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: running ? "not-allowed" : "pointer",
                }}
              >
                {running ? "Running..." : "Run"}
              </button>

              {/* Submit */}
              <button
                onClick={triggerSubmitButton}
                style={{
                  background: "#fff",
                  color: "#1a1a2e",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 24px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
              >
                {isLastQuestion ? "Submit" : "Next Question"}
              </button>

              {isRecording && (
                <span
                  style={{
                    fontSize: 11,
                    color: "#fb7185",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#fb7185",
                      display: "inline-block",
                    }}
                  ></span>
                  REC
                </span>
              )}

              <div
                style={{ fontWeight: "bold", fontSize: 13, color: "#00ff66" }}
              >
                {status}
              </div>
            </div>
          </div>

          {/* ── Question Panel ── */}
          {currentQuestion && (
            <div
              style={{
                height: 260,
                minHeight: 260,
                maxHeight: 260,
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                background: "#1e1e1e",
                borderBottom: ".5px solid #969292",
                marginBottom:"7px",
              }}
            >
              {/* Fixed header — same as Standard Input / Output headers */}
              <div
                style={{
                  padding: 10,
                  background: "#2d2d2d",
                  fontWeight: "bold",
                  fontSize: 15,
                  color: "#ccc",
                  flexShrink: 0,
                  // borderBottom: ".5px solid #ffff",
                  // justifyContent: "center",
                  // textAlign: "center",
                }}
              >
                Question
              </div>

              {/* Scrollable question body — same bg/text as textarea area */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: 10,
                  background: "#1e1e1e",
                  color: "#fff",
                  fontSize: 14,
                  // fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.6,
                  borderBottom: "1px solid #444",
                  userSelect: "none",
                }}
              >
                {currentQuestion.question || currentQuestion.title || ""}
              </div>
            </div>
          )}

          {/* ── Editor Area ── */}
          <div
            className="coding-editor-container"
            style={{ flex: 1, display: "flex", overflow: "hidden" }}
            onCopy={(e) => {
              if (monacoEditorRef.current) {
                const selection = monacoEditorRef.current.getSelection();
                let copiedText = "";
                if (selection.isEmpty()) {
                  const lineNumber = selection.startLineNumber;
                  copiedText =
                    monacoEditorRef.current
                      .getModel()
                      .getLineContent(lineNumber) + "\n";
                } else {
                  copiedText = monacoEditorRef.current
                    .getModel()
                    .getValueInRange(selection);
                }
                internalClipboardRef.current = copiedText;
              }
            }}
            onCut={(e) => {
              if (monacoEditorRef.current) {
                const selection = monacoEditorRef.current.getSelection();
                let copiedText = "";
                if (selection.isEmpty()) {
                  const lineNumber = selection.startLineNumber;
                  copiedText =
                    monacoEditorRef.current
                      .getModel()
                      .getLineContent(lineNumber) + "\n";
                } else {
                  copiedText = monacoEditorRef.current
                    .getModel()
                    .getValueInRange(selection);
                }
                internalClipboardRef.current = copiedText;
              }
            }}
            onPasteCapture={(e) => {
              const pastedText = e.clipboardData.getData("text");
              const normalizedPaste = pastedText.replace(/\r\n/g, "\n");
              const normalizedInternal = (
                internalClipboardRef.current || ""
              ).replace(/\r\n/g, "\n");

              if (normalizedPaste !== normalizedInternal) {
                e.preventDefault();
                e.stopPropagation();
                setShowPasteWarning(true);
              }
            }}
          >
            <div
              style={{
                flex: 3,
                display: "flex",
                flexDirection: "column",
                minHeight: 200,
                overflow: "hidden",
              }}
            >
              <Editor
                height="100%"
                language={languageName() === "cpp" ? "cpp" : languageName()}
                value={code}
                theme="vs-dark"
                onChange={onCodeChange}
                onMount={handleEditorMount}
                options={{
                  fontSize: 16,
                  minimap: { enabled: false },
                  automaticLayout: true,
                }}
              />
            </div>

            {/* Stdin / Output Panel */}
            <div
              className="coding-panel"
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#1e1e1e",
                overflow: "auto",
                height: "calc(100vh - 260px)",
              }}
            >
              <div
                style={{
                  padding: 10,
                  background: "#2d2d2d",
                  fontWeight: "bold",
                  fontSize: 13,
                  color: "#ccc",
                }}
              >
                Standard Input
              </div>
              <>
                <style>
                  {`
      .stdin-textarea::placeholder {
        color: #434242;
        opacity: 1;
      }
    `}
                </style>

                <textarea
                  value={stdin}
                  onChange={(e) => setStdin(e.target.value)}
                  placeholder={getInputPlaceholder(
                    currentQuestion?.sample_input,
                  )}
                  className="stdin-textarea"
                  style={{
                    height: 180,
                    minHeight: 180,
                    resize: "none",
                    padding: 10,
                    background: "#1e1e1e",
                    color: "#fff",
                    border: "none",
                    outline: "none",
                    borderBottom: "1px solid #444",
                    fontSize: 14,
                    fontFamily: "monospace",
                  }}
                />
              </>
              <div
                style={{
                  padding: 10,
                  background: "#2d2d2d",
                  fontWeight: "bold",
                  fontSize: 13,
                  color: "#ccc",
                }}
              >
                Output
              </div>
              <pre
                style={{
                  minHeight: 260,
                  height: 260,
                  maxHeight: "100%",
                  margin: 0,
                  padding: 12,
                  overflowY: "auto",
                  // overflowX: "auto",
                  background: "#111",
                  color: "#00ff66",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: 14,
                  fontFamily: "monospace",
                }}
              >
                {output}
              </pre>
            </div>
          </div>
        </div>

        {/* ── Question Modal ── */}
        {showQuestionModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              backdropFilter: "blur(4px)",
            }}
          >
            <div
              style={{
                background: "#1a1a2e",
                border: "1px solid #4a4a7e",
                borderRadius: 12,
                width: "90%",
                maxWidth: "650px",
                maxHeight: "80vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
                overflow: "hidden",
                fontFamily: "Inter, system-ui, sans-serif",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  borderBottom: "1px solid #2d2d4e",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                >
                  Question Details
                </h3>
                <button
                  onClick={() => setShowQuestionModal(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#a0aec0",
                    cursor: "pointer",
                    fontSize: 24,
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  &times;
                </button>
              </div>

              <div
                style={{
                  padding: "20px",
                  overflowY: "auto",
                  color: "#cbd5e0",
                  fontSize: 14,
                  lineHeight: "1.6",
                  whiteSpace: "pre-wrap",
                  userSelect: "none",
                }}
              >
                <div style={{ color: "#e2e8f0" }}>
                  {currentQuestion?.question}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "12px 20px",
                  borderTop: "1px solid #2d2d4e",
                  background: "#161626",
                }}
              >
                <button
                  onClick={() => setShowQuestionModal(false)}
                  style={{
                    background: "transparent",
                    color: "#c8c8ff",
                    border: "1px solid #4a4a7e",
                    borderRadius: 8,
                    padding: "7px 16px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <WarningModal
          show={showPasteWarning}
          onClose={() => setShowPasteWarning(false)}
          message="Pasting from external sources is disabled during the interview."
        />

        {showValidationModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
            style={{ zIndex: 99999 }}
          >
            <div className="bg-white rounded-lg shadow-lg px-[80px] py-[24px]">
              <h3
                className="flex justify-center app-body font-medium mb-4"
                style={{
                  fontSize: "2rem",
                  marginBottom: "1rem",
                  color: "#ff4d4f",
                }}
              >
                Error
              </h3>
              <p
                className="mb-4 text-pretty text-center app-textbox"
                style={{
                  fontSize: "1rem",
                  color: "#333",
                  marginBottom: "1.5rem",
                }}
              >
                {validationMessage}
              </p>
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="bg-[#2d76f9] text-white px-6 py-1.5 btn-size"
                  style={{
                    backgroundColor: "#ff4d4f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    padding: "0.6rem 1.5rem",
                    fontSize: "1rem",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmationModal && SubmitConfirmationModal}

        {isSubmitting && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70"
            style={{ zIndex: 999999 }}
          >
            <div className="text-white text-xl font-semibold flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting, please wait...
            </div>
          </div>
        )}
        {showSubmitSuccess && (
          <div
            style={{
              position: "fixed",
              top: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 999999,
              background: "#2e7d4f",
              color: "#fff",
              borderRadius: 10,
              padding: "12px 28px",
              fontSize: 15,
              fontWeight: 600,
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              gap: 10,
              animation: "fadeInDown 0.3s ease",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Your response has been submitted!
          </div>
        )}
        <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      </>
    );
  },
);

export default CodingOverlay;
